import React from 'react';

/**
 * RmsFormErrorBoundary
 * --------------------
 * Lightweight error boundary for the RMS form renderer.
 *
 * Why:
 *   A render crash inside the one-question-at-a-time wizard previously left the
 *   user staring at a silently frozen (or blank) page with no way forward except
 *   guessing that a refresh might help. React unmounts the whole tree below the
 *   nearest boundary on a render error, so without one the failure is invisible.
 *
 * What:
 *   Catches render/lifecycle errors from its children, logs them via
 *   console.error (the established pattern in this codebase — see the report
 *   boundary in hfp/HfpV2Report.js), and shows a friendly recovery message
 *   instead of a dead screen. Answers are autosaved step-by-step, so a refresh
 *   resumes from the last saved question.
 */
export default class RmsFormErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[RmsForm] Render crash caught by error boundary:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div role="alert" className="max-w-md w-full text-left space-y-3 p-6 rounded-lg border border-red-200 bg-white">
            <h2 className="text-lg font-bold text-red-700">Something went wrong</h2>
            <p className="text-sm text-gray-700">
              Please refresh the page to continue. Your answers are saved as you
              complete each question, so you can pick up where you left off.
            </p>
            <button
              type="button"
              onClick={() => { try { window.location.reload(); } catch (_) { /* no-op: unavailable outside a browser */ } }}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-opacity"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
